/**
 * This method creates a guard which allows only unknow users entering the public routes. It let
 * them keep browsing otherwise.
 *
 * @param {Object} store - A reference to VUEX`s store.
 * @returns {Function (to, from, next) - void } - A guard to apply before login & remember routes
 */
export function createPublicRoutesGuard(store) {
    return (to, from, next) => {
        if (store.getters.currentUser) {
            next(from)
        } else {
            next()
        }
    }
}

/**
 * This method creates a guard which allows to access the initial setup route only for those
 * logged users that haven't picked up a language or password yet, if (and only if) the platforms
 * requires that sort of initialization.
 *
 * @param {Object} store - A reference to VUEX's store.
 * @returns {Function (to, from, next) - void } - A guard to apply before initial-setup route
 */

export function createInitialSetupGuard(store) {
    return (to, from, next) => {
        const user = store.getters.currentUser
        const initialSetupDone =
            user && !user.loginSteps.language && !user.loginSteps.password

        if (!user || initialSetupDone) {
            next(from)
        } else {
            next()
        }
    }
}

/**
 * This method creates a guard which allows to access the data policy acceptance route
 * only for those users that haven't done it yet, if (and only if) the platform requires this initial step.
 *
 * @param {Object} store - a reference to VUEX's store.
 * @returns { Function (to, next, from) - void } - A guard to apply before entering data-policy route.
 */

export function createDataPolicyGuard(store) {
    return (to, from, next) => {
        const user = store.getters.currentUser
        const dataPolicyAccepted = user && !user.loginSteps.dataPolicy

        if (!user || dataPolicyAccepted) {
            next(from)
        } else {
            next()
        }
    }
}

/**
 * This method prevents users to access spaces for which there is no link available
 *
 * @param {Object} store - a reference to VUEX's store.
 * @returns { Function (to, next, from) - void } - A guard to apply before entering data-policy route.
 */

export function createSpacesGuard(store) {
    return (to, _from, next) => {
        const link = store.state.structure.links.find(l => l.url == to.path)
        if (link) {
            next()
        } else {
            next("not-found")
        }
    }
}

/**
 * Prevents users from accessing communication area if it's not included in the space links
 */

export function createCommunicationGuard(store) {
    return (_to, from, next) => {
        const link = store.state.structure.links.find(
            l => l.url == "/communication"
        )
        if (link) {
            next()
        } else {
            next(from)
        }
    }
}

/**
 * This route prevents logged users entering the app before the fully have met
 * their platform's requirements, if any. I.e., they should've picked up a language,
 * set up a new password and/or accepted the data policy.
 *
 * Once they had completed all these steps, we will let them brows as normally.
 *
 * @param {store} - A reference to VUEX's store.
 * @returns { function (to, from, next) - void } - A guard to apply onBeforeEach route.
 */

export function createAllRoutesGuard(
    store,
    assign = window.location.assign.bind(window.location)
) {
    return (to, from, next) => {
        const { v2ProviderUrl } = store.state.platform
        const user = store.getters.currentUser
        const isPublic = to.matched.some(m => m.meta && m.meta.public)
        const mandatoryLoginSteps = ["initial-setup", "data-policy"]

        // The user is a provider, let's get them to v2.
        if (v2ProviderUrl) {
            assign(v2ProviderUrl)
            next(false)
            return
        }

        // Public route, let them browse.
        if (isPublic) {
            next()
            return
        }

        // No user but private route, log them out
        if (!user) {
            next({ name: "login", query: { redirect: to.fullPath } })
            return
        }

        // User is logged but ... did he completed all the steps?
        if (!mandatoryLoginSteps.includes(to.name)) {
            if (
                from.name !== "initial-setup" &&
                (user.loginSteps.language || user.loginSteps.password)
            ) {
                next({
                    name: "initial-setup",
                    query: { redirect: to.fullPath },
                })
                return
            }

            if (from.name !== "data-policy" && user.loginSteps.dataPolicy) {
                next({ name: "data-policy", query: { redirect: to.fullPath } })
                return
            }
        }

        // No more checks, keep browsing
        next()
    }
}

export function createProfileGuard(store) {
    return (to, from, next) => {
        const { hasExperienceFormation, hasExternalFormation } =
            store.getters.currentUser

        switch (to.path) {
            case "/profile/resume":
                hasExperienceFormation ? next() : next({ name: "not-found" })
                break

            case "/profile/external-training":
                hasExternalFormation ? next() : next({ name: "not-found" })
                break

            default:
                next()
        }
    }
}
